import React, { useRef, useState } from 'react';
import AvatarEditor from 'react-avatar-editor';

import BayFilesUpload from '../../../../BayFilesUpload';
import BayModal from '../../../../BayModal';

import { Button, Col, Form, Row } from 'react-bootstrap';

import '../../EditWidget/EditWidget.scss';
import { useDropzone } from 'react-dropzone';
import { BsTrash3 } from 'react-icons/bs';

interface StageImage {
	img: string | null;
	zoom: number;
	fileName: string | null;
	type: string | null;
}

interface HeaderImageEditorProps {
	editModalInputFields?: any;
	setEditModalInputFields: (fields: any) => void;
	setHeaderImageUrl: (file: File | null) => void;
	widgetId: string;
	t: (key: string, params?: any) => string;
}

const HeaderImageEditor: React.FC<HeaderImageEditorProps> = ({
	editModalInputFields,
	setEditModalInputFields,
	setHeaderImageUrl,
	widgetId,
	t,
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [stageImage, setStageImage] = useState<StageImage>({
		img: editModalInputFields?.[widgetId]?.headerImage || null,
		zoom: 1,
		fileName: null,
		type: null,
	});

	const onDrop = (acceptedFiles: File[]) => {
		if (acceptedFiles?.length > 0) {
			const url = URL.createObjectURL(acceptedFiles[0]);
			setStageImage({
				...stageImage,
				img: url,
				fileName: acceptedFiles[0].name,
				type: acceptedFiles[0].type,
			});
			setIsModalOpen(true);
		}
	};

	const { getRootProps, getInputProps, open } = useDropzone({
		noClick: true,
		accept: {
			'image/jpeg': [],
			'image/png': [],
		},
		onDrop,
	});
	const editorRef = useRef<AvatarEditor>(null);

	const handleSave = () => {
		if (editorRef?.current) {
			const canvas = editorRef.current.getImageScaledToCanvas();
			const base64Image = canvas.toDataURL(stageImage.type || 'image/png');
			canvas?.toBlob((blob) => {
				if (blob) {
					const file = new File(
						[blob],
						stageImage.fileName || 'header-image.png',
						{
							type: stageImage.type || 'image/png',
						},
					);
					setHeaderImageUrl(file);
				}
			}, stageImage.type || 'image/png');
			setEditModalInputFields((prevFields: any) => ({
				...prevFields,
				[widgetId]: {
					...prevFields[widgetId],
					headerImage: base64Image,
				},
			}));
			setIsModalOpen(false);
		}
	};

	return (
		<div className="header-image-editor">
			{editModalInputFields?.[widgetId]?.headerImage ? (
				<div className="d-flex flex-column gap-2">
					<img
						alt="cropped"
						className="header-image"
						crossOrigin="anonymous"
						src={editModalInputFields?.[widgetId]?.headerImage}
					/>
					<div
						className="d-flex flex-row gap-3 align-items-center"
						style={{ padding: '6px 0px' }}
					>
						<span
							className="text-primary fw-medium fs-6 cursor-pointer"
							onClick={() => {
								setIsModalOpen(true);
							}}
						>
							{t('replacePhoto')}
						</span>
						<div className="d-flex align-items-center gap-2 cursor-pointer">
							<BsTrash3 className="text-danger" />
							<span
								className="text-danger fw-medium fs-6"
								onClick={() => {
									setEditModalInputFields((prevFields: any) => ({
										...prevFields,
										[widgetId]: {
											...prevFields[widgetId],
											headerImage: null,
										},
									}));
								}}
							>
								{t('deletePhoto')}
							</span>
						</div>
					</div>
				</div>
			) : (
				<BayFilesUpload
					allowedFileTypes={['jpeg', 'jpg', 'png'] as any}
					allowedFileTypeslabel="Only the following image file formats are allowed:"
					allowedFileTypesText={"JPEG, PNG\nRecommended image size is: 240 × 80" as any}
					noData={true}
					uploadLinkText="Choose images"
					onDrop={onDrop}
				/>
			)}
			<BayModal
				centered={true}
				className="image-editor-modal"
				closeButton={true}
				footer={
					<Row className="w-100 justify-between">
						<Col className="ps-0">
							<Button
								className="px-3 py-2 h-100"
								variant="outline-secondary"
								onClick={() => {
									setIsModalOpen(false);
								}}
							>
								{t('cancel')}
							</Button>
						</Col>
						<Col className="pe-0 d-flex justify-content-end align-items-center gap-3">
							<Button
								className="change-image-button px-3 py-2 h-100"
								style={{ width: '130px' }}
								variant="outline-primary"
								onClick={open}
							>
								{t('changeImage')}
							</Button>
							<Button
								className="px-3 py-2 h-100"
								variant="primary"
								onClick={handleSave}
							>
								{t('save')}
							</Button>
						</Col>
					</Row>
				}
				height="100%"
				show={isModalOpen}
				title="Edit Image"
				onHideFunc={() => {
					setIsModalOpen(false);
				}}
			>
				<div className="d-flex justify-content-center flex-column align-items-center gap-3 mb-5">
					<span className="fw-normal fs-6 text-dark">
						{t('imageCropInstruction')}
					</span>
					<div
						className="crop-image-container d-flex justify-content-center align-items-center overflow-hidden"
						{...getRootProps()}
					>
						<input {...getInputProps()} />
						<AvatarEditor
							ref={editorRef}
							backgroundColor="#ffffff"
							border={24}
							className="crop-image h-100 overflow-hidden"
							color={[0, 0, 0, 0.6]}
							crossOrigin="anonymous"
							height={138}
							image={stageImage?.img || ''}
							scale={stageImage?.zoom || 1}
							width={352}
						/>
					</div>
				</div>
				<div className="range-container mt-3 d-flex justify-content-center">
					<Form.Range
						className="w-100 custom-slider"
						max={2}
						min={1}
						step={0.001}
						value={stageImage?.zoom || 1}
						onChange={(e) =>
							setStageImage({ ...stageImage, zoom: parseFloat(e.target.value) })
						}
					/>
				</div>
			</BayModal>
		</div>
	);
};

export default HeaderImageEditor;
